import { useRoadmapNavigation } from '@griply/hooks';
import { useUpdateUserSettings } from 'features/user';
import { useCallback, useState } from 'react';
import { useToday } from 'shared/contexts/today';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useTodayScrollTime } from 'shared/hooks/use-today-scroll-time';
import { useUser } from 'shared/hooks/use-user';
import { LimitKeys } from 'shared/types/limit-keys';
import { TimelineTimeframes } from 'shared/types/timeline';
import { targetIsInput } from 'shared/utils/target-is-input';

export const useCalendarControls = (captureKeys = true) => {
  const today = useToday();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const { allowed: isGoalsOverviewTimelineAllowed } = usePremiumFeatureAllowed(
    LimitKeys.DesktopGoalsOverviewTimeline,
    {},
  );
  const initialTimeframe =
    user?.settings?.goalsOverview?.roadmap?.timeframe ??
    TimelineTimeframes.Year;

  // Local, not derived from settings: deriving it rendered a switch twice (bars jumped to an intermediate zoom).
  const [timeframe, setTimeframe] = useState(initialTimeframe);
  const {
    referenceDate,
    scrollRequest,
    centerTodayIn,
    onToday: centerToday,
    onNext: goNext,
    onPrev: goPrev,
  } = useRoadmapNavigation({ timeframe, today });

  const onTimeframe = useCallback(
    (newTimeframe: TimelineTimeframes) => {
      if (!isGoalsOverviewTimelineAllowed) {
        return;
      }
      setTimeframe(newTimeframe);
      centerTodayIn(newTimeframe);
      updateUserSettings({
        goalsOverview: { roadmap: { timeframe: newTimeframe } },
      });
    },
    [isGoalsOverviewTimelineAllowed, centerTodayIn, updateUserSettings],
  );

  const onToday = useCallback(() => {
    if (!isGoalsOverviewTimelineAllowed) {
      return;
    }
    centerToday();
  }, [isGoalsOverviewTimelineAllowed, centerToday]);

  const onNext = useCallback(() => {
    if (!isGoalsOverviewTimelineAllowed) {
      return;
    }
    goNext();
  }, [isGoalsOverviewTimelineAllowed, goNext]);

  const onPrev = useCallback(() => {
    if (!isGoalsOverviewTimelineAllowed) {
      return;
    }
    goPrev();
  }, [isGoalsOverviewTimelineAllowed, goPrev]);

  // handle keybindings
  useTodayScrollTime(captureKeys, onToday);

  useKeysDown(
    ['KeyM'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onTimeframe(TimelineTimeframes.Month);
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['KeyQ'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onTimeframe(TimelineTimeframes.Quarter);
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['KeyY'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onTimeframe(TimelineTimeframes.Year);
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['Digit5'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onTimeframe(TimelineTimeframes.FiveYears);
    },
    { otherKeysInCombinationAllowed: true },
  );

  useKeysDown(
    ['ArrowLeft'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onPrev();
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['ArrowRight'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onNext();
    },
    { otherKeysInCombinationAllowed: true },
  );

  return {
    referenceDate,
    scrollRequest,
    timeframe,
    onTimeframe,
    onToday,
    onNext,
    onPrev,
  };
};
